import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/Badge';
import type { ExpenseStatus, SyncStatus } from '@/hooks/useExpenses';

export function ExpenseStatusBadge({ status }: { status: ExpenseStatus }) {
  const { t } = useTranslation();
  const variants: Record<ExpenseStatus, { color: string; label: string }> = {
    pending_invoice:    { color: 'bg-gray-100 text-gray-700',     label: t('expense.status.pending_invoice', 'Pending invoice') },
    pending_allocation: { color: 'bg-amber-100 text-amber-800',   label: t('expense.status.pending_allocation', 'Pending allocation') },
    pending_approval:   { color: 'bg-blue-100 text-blue-800',     label: t('expense.status.pending_approval', 'Pending approval') },
    approved:           { color: 'bg-emerald-100 text-emerald-800', label: t('expense.status.approved', 'Approved') },
    rejected:           { color: 'bg-red-100 text-red-800',       label: t('expense.status.rejected', 'Rejected') },
    cancelled:          { color: 'bg-gray-100 text-gray-500',     label: t('expense.status.cancelled', 'Cancelled') },
  };
  const v = variants[status] ?? variants.pending_approval;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${v.color}`}>{v.label}</span>;
}

export function SyncStatusBadge({ status }: { status: SyncStatus }) {
  const { t } = useTranslation();
  const variants: Record<SyncStatus, { color: string; label: string; icon: string }> = {
    local_only:   { color: 'bg-gray-100 text-gray-600',     label: t('sync.local_only', 'Local'),      icon: '○' },
    pending_push: { color: 'bg-amber-100 text-amber-800',   label: t('sync.pending_push', 'Queued'),   icon: '↑' },
    pushing:      { color: 'bg-blue-100 text-blue-800',     label: t('sync.pushing', 'Syncing...'),    icon: '⟳' },
    synced:       { color: 'bg-emerald-100 text-emerald-800', label: t('sync.synced', 'Synced'),       icon: '✓' },
    pending_pull: { color: 'bg-blue-100 text-blue-800',     label: t('sync.pending_pull', 'Updating'), icon: '↓' },
    conflict:     { color: 'bg-red-100 text-red-800',       label: t('sync.conflict', 'Conflict'),     icon: '⚠' },
    error:        { color: 'bg-red-100 text-red-800',       label: t('sync.error', 'Error'),           icon: '✗' },
  };
  const v = variants[status] ?? variants.local_only;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs ${v.color}`}>
    <span>{v.icon}</span><span>{v.label}</span>
  </span>;
}
